import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Building2, 
  Bed, 
  DollarSign, 
  Plus, 
  Loader2, 
  AlertCircle,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
  Star,
  Clock,
  Calendar,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import roomService, { RoomType, RoomPlan, RateSlab } from '../../services/roomService';
import RoomTypeModal from './RoomTypeModal';
import RoomPlanModal from './RoomPlanModal';
import RateSlabModal from './RateSlabModal';

interface RoomManagementProps {
  hotelId: number;
}

type ViewType = 'room-types' | 'room-plans' | 'rate-slabs';

const RoomManagement: React.FC<RoomManagementProps> = ({ hotelId }) => {
  const [currentView, setCurrentView] = useState<ViewType>('room-types');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Room Types State
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [roomTypePagination, setRoomTypePagination] = useState({ page: 1, limit: 10, total: 0, total_pages: 0 });

  // Room Plans State
  const [roomPlans, setRoomPlans] = useState<RoomPlan[]>([]);
  const [roomPlanPagination, setRoomPlanPagination] = useState({ page: 1, limit: 10, total: 0, total_pages: 0 });
  const [selectedRoomType, setSelectedRoomType] = useState<RoomType | null>(null);

  // Rate Slabs State
  const [rateSlabs, setRateSlabs] = useState<RateSlab[]>([]);
  const [rateSlabPagination, setRateSlabPagination] = useState({ page: 1, limit: 10, total: 0, total_pages: 0 });
  const [selectedRoomPlan, setSelectedRoomPlan] = useState<RoomPlan | null>(null);

  // Modal States
  const [showRoomTypeModal, setShowRoomTypeModal] = useState(false);
  const [editingRoomType, setEditingRoomType] = useState<RoomType | null>(null);
  const [showRoomPlanModal, setShowRoomPlanModal] = useState(false);
  const [editingRoomPlan, setEditingRoomPlan] = useState<RoomPlan | null>(null);
  const [showRateSlabModal, setShowRateSlabModal] = useState(false);
  const [editingRateSlab, setEditingRateSlab] = useState<RateSlab | null>(null);

  // Load room types
  const loadRoomTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const page = roomTypePagination?.page || 1;
      const limit = roomTypePagination?.limit || 10;
      const response = await roomService.getRoomTypes(hotelId, page, limit);
      setRoomTypes(response.data);
      setRoomTypePagination(response.pagination);
    } catch (err) {
      setError('Failed to load room types');
    } finally {
      setLoading(false);
    }
  };

  // Load room plans
  const loadRoomPlans = async (roomTypeId: number) => {
    try {
      setLoading(true);
      setError(null);
      const page = roomPlanPagination?.page || 1;
      const limit = roomPlanPagination?.limit || 10;
      const response = await roomService.getRoomPlans(roomTypeId, page, limit);
      setRoomPlans(response.data);
      setRoomPlanPagination(response.pagination);
    } catch (err) {
      setError('Failed to load room plans');
    } finally {
      setLoading(false);
    }
  };

  // Load rate slabs
  const loadRateSlabs = async (roomPlanId: number) => {
    try {
      setLoading(true);
      setError(null);
      const page = rateSlabPagination?.page || 1;
      const limit = rateSlabPagination?.limit || 10;
      const response = await roomService.getRateSlabs(roomPlanId, page, limit);
      setRateSlabs(response.data);
      setRateSlabPagination(response.pagination);
    } catch (err) {
      setError('Failed to load rate slabs');
    } finally {
      setLoading(false);
    }
  };

  // Handle room type operations
  const handleSaveRoomType = async (roomData: Partial<RoomType>) => {
    try {
      if (editingRoomType) {
        await roomService.updateRoomType(editingRoomType.id, roomData);
      } else {
        await roomService.createRoomType(roomData);
      }
      loadRoomTypes();
    } catch (err: any) {
      throw new Error(err.message || 'Failed to save room type');
    }
  };

  const handleDeleteRoomType = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this room type?')) return;
    
    try {
      setLoading(true);
      await roomService.deleteRoomType(id);
      loadRoomTypes();
    } catch (err) {
      setError('Failed to delete room type');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (id: number, currentStatus: string) => {
    try {
      setLoading(true);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await roomService.toggleRoomTypeStatus(id, newStatus as any);
      loadRoomTypes();
    } catch (err) {
      setError('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  // Handle room plan operations
  const handleSaveRoomPlan = async (roomPlanData: Partial<RoomPlan>) => {
    try {
      if (editingRoomPlan) {
        await roomService.updateRoomPlan(editingRoomPlan.id, roomPlanData);
      } else {
        await roomService.createRoomPlan({
          ...roomPlanData,
          room_type_id: selectedRoomType!.id
        });
      }
      loadRoomPlans(selectedRoomType!.id);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to save room plan');
    }
  };

  const handleDeleteRoomPlan = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this room plan?')) return;
    
    try {
      setLoading(true);
      await roomService.deleteRoomPlan(id);
      loadRoomPlans(selectedRoomType!.id);
    } catch (err) {
      setError('Failed to delete room plan');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomPlanStatusToggle = async (id: number, currentStatus: string) => {
    try {
      setLoading(true);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await roomService.toggleRoomPlanStatus(id, newStatus as any);
      loadRoomPlans(selectedRoomType!.id);
    } catch (err) {
      setError('Failed to update room plan status');
    } finally {
      setLoading(false);
    }
  };

  // Rate Slab Operations
  const handleSaveRateSlab = async (rateSlabData: Partial<RateSlab>) => {
    try {
      if (editingRateSlab) {
        await roomService.updateRateSlab(editingRateSlab.id, rateSlabData);
      } else {
        await roomService.createRateSlab({
          ...rateSlabData,
          room_plan_id: selectedRoomPlan!.id
        });
      }
      loadRateSlabs(selectedRoomPlan!.id);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to save rate slab');
    }
  };

  const handleDeleteRateSlab = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this rate slab?')) return;
    
    try {
      setLoading(true);
      await roomService.deleteRateSlab(id);
      loadRateSlabs(selectedRoomPlan!.id);
    } catch (err) {
      setError('Failed to delete rate slab');
    } finally {
      setLoading(false);
    }
  };

  const handleRateSlabStatusToggle = async (id: number, currentStatus: string) => {
    try {
      setLoading(true);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await roomService.toggleRateSlabStatus(id, newStatus as any);
      loadRateSlabs(selectedRoomPlan!.id);
    } catch (err) {
      setError('Failed to update rate slab status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-red-600 bg-red-100';
      case 'maintenance': return 'text-yellow-600 bg-yellow-100';
      case 'seasonal': return 'text-blue-600 bg-blue-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'inactive': return <XCircle className="w-4 h-4" />;
      case 'maintenance': return <AlertTriangle className="w-4 h-4" />;
      case 'seasonal': return <Calendar className="w-4 h-4" />;
      case 'draft': return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  useEffect(() => {
    if (currentView === 'room-types') {
      loadRoomTypes();
    }
  }, [currentView, roomTypePagination?.page]);

  useEffect(() => {
    if (currentView === 'room-plans') {
      if (selectedRoomType) {
        loadRoomPlans(selectedRoomType.id);
      } else {
        loadRoomTypes();
      }
    }
  }, [currentView, selectedRoomType, roomPlanPagination?.page]);

  useEffect(() => {
    if (currentView === 'rate-slabs') {
      if (selectedRoomPlan) {
        loadRateSlabs(selectedRoomPlan.id);
      } else if (selectedRoomType) {
        loadRoomPlans(selectedRoomType.id);
      } else {
        loadRoomTypes();
      }
    }
  }, [currentView, selectedRoomPlan, selectedRoomType, rateSlabPagination?.page]);

  // Render Room Types View
  const renderRoomTypes = () => (
    <Card>
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Room Types</h3>
            <p className="text-sm text-gray-600">Manage room types for this hotel</p>
          </div>
          <Button onClick={() => {
            setEditingRoomType(null);
            setShowRoomTypeModal(true);
          }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Room Type
          </Button>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex justify-center items-center py-8 text-red-600">
            <AlertCircle className="w-8 h-8 mr-2" />
            {error}
          </div>
        ) : !roomTypes || roomTypes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No room types found</p>
            <p className="text-sm">Click "Add Room Type" to get started</p>
          </div>
        ) : (
          <div className="space-y-4">
            {roomTypes?.map((roomType) => (
              <Card key={roomType.id} className="border border-gray-200">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <h4 className="text-lg font-semibold">{roomType.room_type_name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(roomType.status)}`}>
                          {getStatusIcon(roomType.status)}
                          {roomType.status}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Bed className="w-4 h-4" />
                          {roomType.bed_type} ({roomType.number_of_beds})
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          {roomType.max_adults} adults, {roomType.max_children} children
                        </div>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          {roomType.total_rooms} total, {roomType.rooms_allocated_to_trp} TRP
                        </div>
                        {roomType.room_size_sqft && (
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4" />
                            {roomType.room_size_sqft} sq ft
                          </div>
                        )}
                      </div>

                      {roomType.room_description && (
                        <p className="text-sm text-gray-600 mt-2">{roomType.room_description}</p>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedRoomType(roomType);
                          setCurrentView('room-plans');
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Plans
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingRoomType(roomType);
                          setShowRoomTypeModal(true);
                        }}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusToggle(roomType.id, roomType.status)}
                      >
                        {roomType.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteRoomType(roomType.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {roomTypePagination?.total_pages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={roomTypePagination?.page === 1}
              onClick={() => setRoomTypePagination({ ...roomTypePagination, page: (roomTypePagination?.page || 1) - 1 })}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">
              Page {roomTypePagination?.page || 1} of {roomTypePagination?.total_pages || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={roomTypePagination?.page === roomTypePagination?.total_pages}
              onClick={() => setRoomTypePagination({ ...roomTypePagination, page: (roomTypePagination?.page || 1) + 1 })}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Render Room Plans View
  const renderRoomPlans = () => (
    <Card>
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Room Plans</h3>
            <p className="text-sm text-gray-600">
              {selectedRoomType ? `Plans for ${selectedRoomType.room_type_name}` : 'Select a room type to view plans'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentView('room-types')}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Room Types
            </Button>
            {selectedRoomType && (
              <Button onClick={() => {
                setEditingRoomPlan(null);
                setShowRoomPlanModal(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Room Plan
              </Button>
            )}
          </div>
        </div>

        {!selectedRoomType ? (
          <div className="text-center py-8 text-gray-500">
            <Bed className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Please select a room type to view its plans</p>
            <Button 
              onClick={() => setCurrentView('room-types')}
              className="mt-4"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Room Types
            </Button>
          </div>
        ) : (
          <>
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex justify-center items-center py-8 text-red-600">
                <AlertCircle className="w-8 h-8 mr-2" />
                {error}
              </div>
            ) : !roomPlans || roomPlans.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bed className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No room plans found for this room type</p>
                <p className="text-sm">Click "Add Room Plan" to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {roomPlans?.map((plan) => (
                  <Card key={plan.id} className="border border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-2">
                            <h4 className="text-lg font-semibold">{plan.plan_name}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(plan.status)}`}>
                              {getStatusIcon(plan.status)}
                              {plan.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              {plan.occupancy_type} ({plan.adults_included} adults, {plan.children_included} children)
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4" />
                              ₹{plan.base_rate} base rate
                            </div>
                            <div className="flex items-center gap-2">
                              <Star className="w-4 h-4" />
                              {plan.meal_plan}
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              {plan.is_refundable ? 'Refundable' : 'Non-refundable'}
                            </div>
                          </div>

                                                     {plan.meal_plan_description && (
                             <p className="text-sm text-gray-600 mt-2">{plan.meal_plan_description}</p>
                           )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedRoomPlan(plan);
                              setCurrentView('rate-slabs');
                            }}
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            Rates
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingRoomPlan(plan);
                              setShowRoomPlanModal(true);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRoomPlanStatusToggle(plan.id, plan.status)}
                          >
                            {plan.status === 'active' ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteRoomPlan(plan.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {roomPlanPagination?.total_pages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={roomPlanPagination?.page === 1}
                  onClick={() => setRoomPlanPagination({ ...roomPlanPagination, page: (roomPlanPagination?.page || 1) - 1 })}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {roomPlanPagination?.page || 1} of {roomPlanPagination?.total_pages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={roomPlanPagination?.page === roomPlanPagination?.total_pages}
                  onClick={() => setRoomPlanPagination({ ...roomPlanPagination, page: (roomPlanPagination?.page || 1) + 1 })}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  // Render Rate Slabs View
  const renderRateSlabs = () => (
    <Card>
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Rate Slabs</h3>
            <p className="text-sm text-gray-600">
              {selectedRoomPlan ? `Rates for ${selectedRoomPlan.plan_name}` : 
               selectedRoomType ? `Select a plan for ${selectedRoomType.room_type_name}` : 
               'Select a room type and plan to view rates'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentView('room-plans')}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Plans
            </Button>
            {selectedRoomPlan && (
              <Button onClick={() => {
                setEditingRateSlab(null);
                setShowRateSlabModal(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Rate Slab
              </Button>
            )}
          </div>
        </div>

        {!selectedRoomPlan ? (
          <div className="text-center py-8 text-gray-500">
            <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Please select a room plan to manage its rates</p>
            <Button 
              onClick={() => setCurrentView('room-plans')}
              className="mt-4"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Room Plans
            </Button>
          </div>
        ) : (
          <>
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex justify-center items-center py-8 text-red-600">
                <AlertCircle className="w-8 h-8 mr-2" />
                {error}
              </div>
            ) : !rateSlabs || rateSlabs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No rate slabs found for this room plan</p>
                <p className="text-sm">Click "Add Rate Slab" to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {rateSlabs?.map((slab) => (
                  <Card key={slab.id} className="border border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-2">
                            <h4 className="text-lg font-semibold">
                              {slab.slab_name || `Rate Slab ${slab.id}`}
                            </h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(slab.status)}`}>
                              {getStatusIcon(slab.status)}
                              {slab.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4" />
                              ₹{slab.rate_per_night} per night
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              {new Date(slab.valid_from).toLocaleDateString()} - {new Date(slab.valid_to).toLocaleDateString()}
                            </div>
                          </div>

                          {slab.slab_description && (
                            <p className="text-sm text-gray-600 mt-2">{slab.slab_description}</p>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingRateSlab(slab);
                              setShowRateSlabModal(true);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRateSlabStatusToggle(slab.id, slab.status)}
                          >
                            {slab.status === 'active' ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteRateSlab(slab.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {rateSlabPagination?.total_pages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={rateSlabPagination?.page === 1}
                  onClick={() => setRateSlabPagination({ ...rateSlabPagination, page: (rateSlabPagination?.page || 1) - 1 })}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {rateSlabPagination?.page || 1} of {rateSlabPagination?.total_pages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={rateSlabPagination?.page === rateSlabPagination?.total_pages}
                  onClick={() => setRateSlabPagination({ ...rateSlabPagination, page: (rateSlabPagination?.page || 1) + 1 })}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Main Content */}
      {currentView === 'room-types' && renderRoomTypes()}
      {currentView === 'room-plans' && renderRoomPlans()}
      {currentView === 'rate-slabs' && renderRateSlabs()}

      {/* Room Type Modal */}
      <RoomTypeModal
        isOpen={showRoomTypeModal}
        onClose={() => {
          setShowRoomTypeModal(false);
          setEditingRoomType(null);
        }}
        onSave={handleSaveRoomType}
        roomType={editingRoomType}
        hotelId={hotelId}
      />

      {/* Room Plan Modal */}
      <RoomPlanModal
        isOpen={showRoomPlanModal}
        onClose={() => {
          setShowRoomPlanModal(false);
          setEditingRoomPlan(null);
        }}
        onSave={handleSaveRoomPlan}
        roomPlan={editingRoomPlan}
      />

      {/* Rate Slab Modal */}
      <RateSlabModal
        isOpen={showRateSlabModal}
        onClose={() => {
          setShowRateSlabModal(false);
          setEditingRateSlab(null);
        }}
        onSave={handleSaveRateSlab}
        rateSlab={editingRateSlab}
        roomPlanName={selectedRoomPlan?.plan_name}
      />
    </div>
  );
};

export default RoomManagement;
